import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import OrderStatusBadge from "./OrderStatusBadge";
import OrderDetailsModal from "./OrderDetailsModal";
import AssignOrderModal from "./AssignOrderModal";
import EditOrderModal from "./EditOrderModal"; // إضافة استيراد مودال التعديل
import { useTranslation } from "react-i18next";
import { updateAgentOrderService } from "redux/services/orders_service";
import { useDispatch } from "react-redux";
import FiltersBar from "./FiltersBar";

const statusIcons = {
  Delivered: (
    <svg
      className="w-5 h-5 text-green-500"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M20 7L9 18l-5-5" />
    </svg>
  ),
  Pending: (
    <svg
      className="w-5 h-5 text-orange-400"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M9 12h6m-3-3v6" />
    </svg>
  ),
  Cancelled: (
    <svg
      className="w-5 h-5 text-red-500"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Shipped: (
    <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
      <path d="M3 10h18v4H3z" />
    </svg>
  ),
};

const OrderTable = ({
  orders,
  selectedOrders,
  onOrderSelect,
  onSelectAll,
  sortConfig,
  onSort,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [editOrder, setEditOrder] = useState(null); // إضافة state للطلب المختار للتعديل

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    onSort({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <Icon name="ArrowUp" size={14} className="text-primary" />
    ) : (
      <Icon name="ArrowDown" size={14} className="text-primary" />
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const toggleExpanded = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const openOrderDetails = (order) => {
    setSelectedOrderDetails(order);
  };

  const closeOrderDetails = () => {
    setSelectedOrderDetails(null);
  };

  const openAssignOrderModal = (orderId) => {
    setAssignOrderId(orderId);
  };

  const closeAssignOrderModal = () => {
    setAssignOrderId(null);
  };

  const handleAssignOrder = () => {
    // يمكن إضافة منطق إضافي هنا إذا لزم الأمر، مثل تحديث القائمة
    closeAssignOrderModal();
  };

  const openEditOrderModal = (order) => {
    setEditOrder(order);
  };

  const closeEditOrderModal = () => {
    setEditOrder(null);
  };

  const handleUpdateOrder = () => {
    // يمكن إضافة منطق لتحديث القائمة هنا
    closeEditOrderModal();
  };

  return (
    <>
      {/* Desktop Table */}
      <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200">
        {/*<FiltersBar />*/}
        <table className="w-full text-sm text-gray-700">
          <thead className="bg-green-50 text-green-900 font-semibold">
            <tr>
              <th className="px-4 py-3 text-center">
                <button
                  onClick={() => handleSort("orderNumber")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("orderNumber")}</span>
                </button>
              </th>

              <th className="py-3 text-center">
                <button
                  onClick={() => handleSort("customerName")}
                  //    className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("customer")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-center">
                <button
                  onClick={() => handleSort("orderDate")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("date")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-center">
                <span>{t("status")}</span>
              </th>

              <th className="px-4 py-3 text-center">
                <button
                  onClick={() => handleSort("total")}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-micro"
                >
                  <span>{t("total")}</span>
                </button>
              </th>

              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  {t("items")}
                </span>
              </th>

              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  الملاحظات
                </span>
              </th>

              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  المنطقة
                </span>
              </th>

              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  العنوان
                </span>
              </th>

              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  تفاصيل الدفع
                </span>
              </th>

              <th className="w-24 px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  {t("actions")}
                </span>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {orders.map((order) => (
              <React.Fragment key={order.id}>
                <tr className="hover:bg-muted/50 transition-micro">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-medium text-foreground">
                        #{order?.id}
                      </span>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User " size={16} className="text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {order?.customer?.first_name}{" "}
                          {order?.customer?.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order?.customer?.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground">
                      {formatDate(order?.created_at)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(order?.created_at).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <OrderStatusBadge
                      status={order?.order_status_name}
                      onUpdateStatus={(newStatus) => {
                        dispatch(
                          updateAgentOrderService({
                            id: order?.id,
                            status: newStatus,
                          })
                        ).then((response) => {
                          if (response?.payload?.status == 200) {
                            // Handle success
                          }
                        });
                      }}
                    />
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">
                      {formatCurrency(order?.total_amount)}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground">
                      {order?.items?.length} {t("items")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {order?.products
                        ?.slice(0, 2)
                        .map((item) => item.name)
                        .join(", ")}
                      {order?.items?.length > 2 &&
                        ` +${order?.items?.length - 2} more`}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">
                      {order?.notes}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">
                      {order?.area?.name}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">
                      {order?.address}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">
                      {order?.payment?.payment_amount}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center space-x-2 mr-2 ml-2 gap-4">
                      <button
                        variant="ghost"
                        size="icon"
                        onClick={() => openOrderDetails(order)}
                        title={t("showOrder")}
                      >
                        <Icon name="Edit" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded Row Details */}
                {expandedOrder === order.id && (
                  <tr>
                    <td colSpan="8" className="px-4 py-4 bg-muted/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Shipping Information */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-foreground flex items-center space-x-2">
                            <Icon
                              name="Truck"
                              size={16}
                              className="text-primary"
                            />
                            <span>{t("shippingAddress")}</span>
                          </h4>
                          <div className="text-sm text-muted-foreground">
                            <div>{order?.shippingAddress?.street}</div>
                            <div>
                              {order?.shippingAddress?.city},{" "}
                              {order?.shippingAddress?.state}{" "}
                              {order?.shippingAddress?.zipCode}
                            </div>
                            <div>{order?.shippingAddress?.country}</div>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-foreground flex items-center space-x-2">
                            <Icon
                              name="Package"
                              size={16}
                              className="text-primary"
                            />
                            <span>{t("orderItems")}</span>
                          </h4>
                          <div className="space-y-1">
                            {order?.products?.map((item, index) => (
                              <div
                                key={index}
                                className="flex justify-between text-sm"
                              >
                                <span className="text-foreground">
                                  {item?.name} x{item?.quantity}
                                </span>
                                <span className="text-muted-foreground">
                                  {formatCurrency(item?.price * item?.quantity)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Payment Information */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-foreground flex items-center space-x-2">
                            <Icon
                              name="CreditCard"
                              size={16}
                              className="text-primary"
                            />
                            <span>{t("paymentDetails")}</span>
                          </h4>
                          <div className="text-sm text-muted-foreground">
                            <div>Method: {order?.paymentMethod}</div>
                            <div>Status: {order?.paymentStatus}</div>
                            <div>Transaction ID: {order?.transactionId}</div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {orders.map((order) => (
          <div key={order.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedOrders.includes(order.id)}
                  onChange={(e) => onOrderSelect(order.id, e.target.checked)}
                />
                <div>
                  <div className="font-mono text-sm font-medium text-foreground">
                    #{order.orderNumber}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(order.orderDate)}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <OrderStatusBadge status={order.status} size="sm" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openOrderDetails(order)}
                >
                  <Icon name="Eye" size={16} />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon
                  name="User "
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-sm text-foreground">
                  {order.customerName}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon
                    name="Package"
                    size={14}
                    className="text-muted-foreground"
                  />
                  <span className="text-sm text-muted-foreground">
                    {order.itemCount} {t("items")}
                  </span>
                </div>
                <div className="font-semibold text-foreground">
                  {formatCurrency(order.total)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order Details Modal */}
      {selectedOrderDetails && (
        <OrderDetailsModal
          order={selectedOrderDetails}
          onClose={closeOrderDetails}
        />
      )}

      {/* Edit Order Modal */}
      {editOrder && (
        <EditOrderModal
          order={editOrder}
          onClose={closeEditOrderModal}
          onUpdateOrder={handleUpdateOrder}
        />
      )}
    </>
  );
};

export default OrderTable;
